import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  styled,
  Alert,
  Skeleton,
} from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { useMutation, useQuery, useLazyQuery, useApolloClient } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { UserContext, NavigationContext, useActionPopoverContext } from 'op-contexts/index';
import { CurrentAppConfig } from '../../Careplan/AppConfig';
import { isDemo } from 'op-utils/helpers';
import { FeedbackModal, useFeedbackHandler } from 'shared-components/components/AiFeedback';
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar';

import CitationPanel, { CitationProps } from './CitationPanel';
import CitationOverlay from './CitationOverlay';
import ChatHeader from './ChatHeader';
import PreCutoffDisclaimer from './PreCutoffDisclaimer';
import { AiAskBar } from './AiAskBar';
import { ChatHistoryItem, ChatRequest, NEW_CONVERSATION_PLACEHOLDER } from './Interface';
import { ChatMessage } from './ChatMessage';
import { ChatConversationList } from './ChatConversationList';
import {
  CONVERSATIONS_QUERY,
  DELETE_CONVERSATION_MUTATION,
  RENAME_CONVERSATION_MUTATION,
  USER_ROLE_QUERY,
  UPSERT_AI_LETTER_MUTATION,
  SEND_AI_LETTER_TO_FHIR_MUTATION,
  AI_LETTER_QUERY,
  AI_CONFIG_QUERY,
} from 'oncology_portal/src/pages/RO/AiSummary/graphql';
import { AiConfigQueryData } from 'op-interfaces/graph/AiConfigQuery';
import { useChatHistory, useDelayedRefetch } from './utils/chatHooks';
import { useConversationState } from './utils/useConversationState';
import { streamChatResponse } from './utils/chatStreaming';
import { useChatAutoScroll } from './utils/useChatAutoScroll';
import { generateProstateMRIReferralTemplateCitation } from './utils/chatUtils';
import { isDocumentType, getDocumentDisplayName } from './utils/documentTypes';
import { theme } from 'theme';
import { dialogButtonSx } from './Chat.styles';
import { useActionPopoverControl } from 'op-contexts';
import { Logger } from 'shared-components/utils';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

const logger = new Logger('ChatPanel');

// Styled Components
const ChatPanelContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  position: 'relative',
  flex: 1,
});

const ChatContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInitial',
})<{ isInitial?: boolean }>(({ isInitial }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: isInitial ? 0 : 1,
  padding: '0px 24px',
  overflow: 'auto',
  height: isInitial ? '180px' : '0',
  minHeight: '180px',
}));

const ChatBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isHidden',
})<{ isHidden: boolean }>(({ isHidden }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  visibility: isHidden ? 'hidden' : 'visible',
  overflowY: 'auto',
  padding: '32px 0px 0px 0px',
}));

const ChatInputContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInitial' && prop !== 'isPopoverLayout',
})<{ isInitial?: boolean; isPopoverLayout?: boolean }>(({ theme, isPopoverLayout }) => ({
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.3s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  padding: '8px 24px 24px 24px',
  paddingBottom: isPopoverLayout ? '4px' : '36px',
}));

// Types
interface InterruptedMessage {
  text: string;
  conversationId?: number;
}

interface CitationSelection {
  citations: CitationProps[];
  activeIndex: number;
}

interface ChatPanelProps {
  patientId: string;
  citationSelection: CitationSelection | null;
  setCitationSelection: (selection: CitationSelection | null) => void;
  chatHistory: ChatHistoryItem;
  setChatHistory: (data: ChatHistoryItem, index: number) => void;
  index: number;
  refetchConversations?: () => void;
  isInitialising?: boolean;
  isLoadingConversation?: boolean;
  selectedConversationId?: number | null;
  setPendingTitleUpdateConversationId?: (id: number | null) => void;
  streamingAbortControllerRef: React.MutableRefObject<AbortController | null>;
  interruptedMessage: InterruptedMessage | null;
  setInterruptedMessage: (message: InterruptedMessage | null) => void;
  isPopoverLayout?: boolean;
  onScrollStateChange?: (isScrolled: boolean) => void;
  // Letter canvas props
  openLetterCanvas: (
    content: string,
    conversationId?: number,
    documentType?: string,
    messageCreatedAt?: string,
    isNewDocument?: boolean,
  ) => Promise<void>;
  updateLetterCanvas: (content: string) => void;
  getActiveDocumentHtml: () => string | undefined;
  isLetterCanvasOpen: boolean;
  letterConversationId?: number | null;
  /** Current document type when letter canvas is open (e.g., 'hpi_document') */
  letterType?: string;
  hasDocumentGenerationFeature?: boolean;
  setAiLetterId?: (id: number | null) => void;
  /** Callback when receiving response state changes (for disabling editor during generation) */
  onReceivingResponseChange?: (isReceiving: boolean) => void;
}

// Helper to create a new chat request
const createRequest = (text: string, response = '', citations: CitationProps[] = []): ChatRequest => ({
  text,
  createdAt: new Date().toISOString(),
  response,
  citations,
  type: '',
});

// Helper to update request in array - matches by text AND createdAt to support duplicate questions
const updateRequest = (
  requests: ChatRequest[],
  text: string,
  updates: Partial<ChatRequest>,
  createdAt?: string,
): ChatRequest[] => {
  // If createdAt is provided, match by both text and createdAt for uniqueness
  // Otherwise fall back to matching by text only (for backwards compatibility)
  const idx = createdAt
    ? requests.findIndex((r) => r.text === text && r.createdAt === createdAt)
    : requests.findIndex((r) => r.text === text && !r.response);
  if (idx === -1) return requests;
  return requests.map((r, i) => (i === idx ? { ...r, ...updates } : r));
};

// Citation type map (for non-document types that use the citation panel)
const CITATION_GENERATORS: Record<string, (patientId: string) => CitationProps> = {
  prostate_mri_referral: generateProstateMRIReferralTemplateCitation,
};

// ============================================================================
// ChatHistory Component
// ============================================================================
interface ChatHistoryProps {
  patientId: string;
  isPopoverLayout?: boolean;
  citationSelection: CitationSelection | null;
  setCitationSelection: (selection: CitationSelection | null) => void;
  openLetterCanvas: (
    content: string,
    conversationId?: number,
    documentType?: string,
    messageCreatedAt?: string,
    isNewDocument?: boolean,
  ) => Promise<void>;
  updateLetterCanvas: (content: string) => void;
  getActiveDocumentHtml: () => string | undefined;
  isLetterCanvasOpen: boolean;
  letterConversationId?: number | null;
  /** Current document type when letter canvas is open (e.g., 'hpi_document') */
  letterType?: string;
  setOnLetterSaved: (callback: (conversationId: number, messageCreatedAt: string, newHtml: string) => void) => void;
  onSelectedConversationIdChange?: (conversationId: number | null) => void;
  onConversationSelected?: () => void;
  onScrollStateChange?: (isScrolled: boolean) => void;
  renderHistoryWrapper?: (list: React.ReactNode) => React.ReactNode;
  hasDocumentGenerationFeature?: boolean;
  startNewConversationRef?: React.MutableRefObject<(() => void) | null>;
  setAiLetterId?: (id: number | null) => void;
  /** Callback when receiving response state changes (for disabling editor during generation) */
  onReceivingResponseChange?: (isReceiving: boolean) => void;
}

const ChatHistory = ({
  patientId,
  isPopoverLayout = false,
  citationSelection,
  setCitationSelection,
  openLetterCanvas,
  updateLetterCanvas,
  getActiveDocumentHtml,
  isLetterCanvasOpen,
  letterConversationId,
  letterType,
  setOnLetterSaved,
  onSelectedConversationIdChange,
  onConversationSelected,
  onScrollStateChange,
  renderHistoryWrapper,
  startNewConversationRef,
  hasDocumentGenerationFeature = false,
  setAiLetterId,
  onReceivingResponseChange,
}: ChatHistoryProps): JSX.Element => {
  const [value, setValue] = useState(0);
  const userContext = useContext(UserContext);
  const timezone = userContext.state.timezone || CurrentAppConfig.DefaultTimezone;

  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; index: number } | null>(null);
  // Track dialog open state separately from data to prevent content flash during exit animation
  const [deleteDialogData, setDeleteDialogData] = useState<{
    conversationId: number;
    index: number;
    title: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteErrorSnackbarOpen, setDeleteErrorSnackbarOpen] = useState(false);

  const [editingConversationId, setEditingConversationId] = useState<number | null>(null);
  const streamingAbortControllerRef = useRef<AbortController | null>(null);
  const [interruptedMessage, setInterruptedMessage] = useState<InterruptedMessage | null>(null);

  const {
    chatHistory,
    setChatHistory,
    selectedConversationId,
    setSelectedConversationId,
    isInitialising,
    isLoadingConversation,
    refetchConversations,
  } = useChatHistory(patientId, timezone, setValue);
  const setPendingTitleUpdateConversationId = useDelayedRefetch(refetchConversations);

  const { dialogProps: actionPopoverDialogProps } = useActionPopoverControl({
    open: deleteDialogOpen,
    enableActionPopover: false,
  });

  const parsedPatientId = parseInt(patientId, 10);
  const [deleteConversation] = useMutation(DELETE_CONVERSATION_MUTATION, {
    refetchQueries: [{ query: CONVERSATIONS_QUERY, variables: { patientId: parsedPatientId } }],
  });
  const [renameConversation] = useMutation(RENAME_CONVERSATION_MUTATION, {
    refetchQueries: [{ query: CONVERSATIONS_QUERY, variables: { patientId: parsedPatientId } }],
  });

  // Update a message's letterHtml when the letter is saved
  const handleLetterSaved = useCallback(
    (conversationId: number, messageCreatedAt: string, newHtml: string) => {
      setChatHistory((prev) => {
        const updated = prev.map((item) => {
          if (item.conversationId !== conversationId) return item;
          const updatedItem = {
            ...item,
            requests: item.requests.map((req) => {
              if (req.createdAt !== messageCreatedAt) return req;
              return { ...req, letterHtml: newHtml };
            }),
          };
          return updatedItem;
        });
        return updated;
      });
    },
    [setChatHistory],
  );

  // Register the callback with ChatContainer so it can call us after save
  useEffect(() => {
    setOnLetterSaved(handleLetterSaved);
  }, [setOnLetterSaved, handleLetterSaved]);

  const handleChatHistoryChange = useCallback(
    (data: ChatHistoryItem, idx: number) => {
      if (!data.conversationId) return;

      setChatHistory((prev) => {
        const updated = [...prev];
        const existing = updated.findIndex((item) => item.conversationId === data.conversationId);

        if (existing !== -1) {
          updated[existing] = data;
        } else {
          const todayIdx = updated.findIndex((item) => item.kind === 'separator' && item.title === 'Today');
          let insertAt = todayIdx !== -1 ? todayIdx + 1 : 1;
          if (todayIdx === -1) {
            updated.splice(1, 0, { title: 'Today', requests: [], kind: 'separator' });
            // After inserting separator at position 1, conversation should be inserted at position 2
            insertAt = 2;
          }
          updated.splice(insertAt, 0, data);

          // For new conversations (idx === 0), set selectedConversationId to highlight in left nav
          // Delay setting it to allow chatHistory to update first, preventing loading state
          // The conversation is already displayed via localConversation, so we just need nav highlighting
          if (idx === 0) {
            // Delay to ensure chatHistory is updated and conversation exists before query runs
            // This prevents the query from triggering a loading state since the conversation
            // will already exist in chatHistory with requests, so useEffect will preserve them
            setTimeout(() => {
              // Only set if we're still on the new conversation slot (value === 0)
              // This ensures we don't trigger a query if user has switched away
              if (value === 0) {
                setSelectedConversationId(data.conversationId!);
              }
            }, 50);
          }
        }
        return updated;
      });
    },
    [setChatHistory, setSelectedConversationId, value],
  );

  const handleConversationClick = useCallback(
    (_idx: number, conversationId?: number) => {
      logDatadogRumCustomAction('ai_chat_select_conversation', {
        patientId,
        viewMode: isPopoverLayout ? 'popover' : 'default',
        conversationId: conversationId ?? null,
      });
      // Only set selectedConversationId - let useChatHistory hook handle tab index synchronization
      // This ensures the conversation data is loaded before switching tabs
      setSelectedConversationId(conversationId || null);
      onConversationSelected?.();
    },
    [setSelectedConversationId, onConversationSelected, patientId, isPopoverLayout],
  );

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>, idx: number) => {
    e.stopPropagation();
    setMenuAnchor({ el: e.currentTarget, index: idx });
  }, []);

  const handleMenuClose = useCallback(() => setMenuAnchor(null), []);

  const handleRename = useCallback(
    async (conversationId: number, newTitle: string): Promise<boolean> => {
      const result = await renameConversation({
        variables: { conversationId, title: newTitle },
      }).catch(() => null);

      if (result?.data?.renameConversation) {
        logDatadogRumCustomAction('ai_chat_rename_conversation', {
          patientId,
          viewMode: isPopoverLayout ? 'popover' : 'default',
          conversationId,
        });
        setChatHistory((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex((item) => item.conversationId === conversationId);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], title: newTitle };
          }
          return updated;
        });
        return true;
      }
      return false;
    },
    [renameConversation, setChatHistory, patientId, isPopoverLayout],
  );

  const handleDeleteClick = useCallback(() => {
    if (!menuAnchor) return;
    const item = chatHistory[menuAnchor.index];
    if (item.conversationId) {
      setDeleteDialogData({ conversationId: item.conversationId, index: menuAnchor.index, title: item.title });
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  }, [menuAnchor, chatHistory, handleMenuClose]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialogData) return;

    // Attempt to delete the conversation
    const result = await deleteConversation({
      variables: { conversationId: deleteDialogData.conversationId },
    }).catch(() => null);

    // Only update local state if deletion was successful
    if (!result?.data?.deleteConversation?.success) {
      setDeleteDialogOpen(false);
      setDeleteErrorSnackbarOpen(true);
      return;
    }

    logDatadogRumCustomAction('ai_chat_delete_conversation', {
      patientId,
      viewMode: isPopoverLayout ? 'popover' : 'default',
      conversationId: deleteDialogData.conversationId,
    });

    // Deletion successful - update local state
    const deleteIdx = chatHistory.findIndex((item) => item.conversationId === deleteDialogData.conversationId);
    if (deleteIdx !== -1) {
      setChatHistory((prev) => prev.filter((_, i) => i !== deleteIdx));
      if (value === deleteIdx) {
        setValue(0);
      } else if (value > deleteIdx) {
        setValue(value - 1);
      }
    }
    // Clear selectedConversationId if we deleted that conversation
    if (selectedConversationId === deleteDialogData.conversationId) {
      setSelectedConversationId(null);
    }
    setDeleteDialogOpen(false);
  }, [
    deleteDialogData,
    deleteConversation,
    setChatHistory,
    chatHistory,
    value,
    selectedConversationId,
    setSelectedConversationId,
    setValue,
    patientId,
    isPopoverLayout,
  ]);

  useEffect(() => {
    if (onSelectedConversationIdChange) {
      onSelectedConversationIdChange(selectedConversationId);
    }
  }, [onSelectedConversationIdChange, selectedConversationId]);

  useEffect(() => {
    if (!startNewConversationRef) return;
    const startNewConversation = () => {
      setSelectedConversationId(null);
      setValue(0);
    };
    startNewConversationRef.current = startNewConversation;
    return () => {
      if (startNewConversationRef.current === startNewConversation) {
        startNewConversationRef.current = null;
      }
    };
  }, [setSelectedConversationId, setValue, startNewConversationRef]);

  const chatConversationList = (
    <ChatConversationList
      chatHistory={chatHistory}
      value={value}
      selectedConversationId={selectedConversationId}
      onConversationClick={handleConversationClick}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      menuAnchor={menuAnchor}
      onRename={handleRename}
      onDeleteClick={handleDeleteClick}
      editingConversationId={editingConversationId}
      setEditingConversationId={setEditingConversationId}
      limitWidth={!isPopoverLayout}
    />
  );

  const renderedChatConversationList = renderHistoryWrapper
    ? renderHistoryWrapper(chatConversationList)
    : chatConversationList;

  const currentChat = value === 0 ? NEW_CONVERSATION_PLACEHOLDER : chatHistory[value] || NEW_CONVERSATION_PLACEHOLDER;

  return (
    <Box sx={{ height: '100%', width: '100%', flex: 1, minHeight: 0 }}>
      <Stack direction="row" height="100%" width="100%" flex={1}>
        {/* Inline conversation history */}
        {!isPopoverLayout && renderedChatConversationList}

        {/* Main chat panel */}
        <ChatPanel
          patientId={patientId}
          chatHistory={currentChat}
          setChatHistory={handleChatHistoryChange}
          index={value}
          citationSelection={citationSelection}
          setCitationSelection={setCitationSelection}
          refetchConversations={refetchConversations}
          isInitialising={isInitialising}
          isLoadingConversation={isLoadingConversation}
          selectedConversationId={selectedConversationId}
          setPendingTitleUpdateConversationId={setPendingTitleUpdateConversationId}
          streamingAbortControllerRef={streamingAbortControllerRef}
          interruptedMessage={interruptedMessage}
          setInterruptedMessage={setInterruptedMessage}
          isPopoverLayout={isPopoverLayout}
          onScrollStateChange={onScrollStateChange}
          openLetterCanvas={openLetterCanvas}
          updateLetterCanvas={updateLetterCanvas}
          getActiveDocumentHtml={getActiveDocumentHtml}
          isLetterCanvasOpen={isLetterCanvasOpen}
          letterConversationId={letterConversationId}
          letterType={letterType}
          hasDocumentGenerationFeature={hasDocumentGenerationFeature}
          setAiLetterId={setAiLetterId}
          onReceivingResponseChange={onReceivingResponseChange}
        />
      </Stack>

      {/* Drawer conversation history */}
      {isPopoverLayout && renderedChatConversationList}

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        {...actionPopoverDialogProps}
        TransitionProps={{
          onExited: () => setDeleteDialogData(null), // Clear data after animation completes
        }}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete "
            <Typography display="inline" fontWeight={500}>
              {deleteDialogData?.title}
            </Typography>
            ". This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button sx={dialogButtonSx} onClick={() => setDeleteDialogOpen(false)}>
            CANCEL
          </Button>
          <Button sx={dialogButtonSx} onClick={handleDeleteConfirm}>
            <Typography fontWeight={500} color={theme.palette.error.main}>
              DELETE
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Error Snackbar */}
      <NotificationSnackbar
        open={deleteErrorSnackbarOpen}
        onClose={() => setDeleteErrorSnackbarOpen(false)}
        type="error"
        message="Failed to delete conversation. Please try again."
      />
    </Box>
  );
};

// ============================================================================
// ChatPanel Component
// ============================================================================
const ChatPanel = ({
  patientId,
  citationSelection,
  setCitationSelection,
  chatHistory,
  setChatHistory,
  index,
  refetchConversations,
  isInitialising = false,
  isLoadingConversation = false,
  selectedConversationId,
  setPendingTitleUpdateConversationId,
  streamingAbortControllerRef,
  interruptedMessage,
  setInterruptedMessage,
  isPopoverLayout = false,
  onScrollStateChange,
  openLetterCanvas,
  updateLetterCanvas,
  getActiveDocumentHtml,
  isLetterCanvasOpen,
  letterConversationId,
  letterType,
  hasDocumentGenerationFeature = false,
  setAiLetterId,
  onReceivingResponseChange,
}: ChatPanelProps): JSX.Element => {
  const [isInputCentered, setIsInputCentered] = useState(true);
  const [selectedCitation, setSelectedCitation] = useState<string | null>(null);

  const [hoveredMessageIndex, setHoveredMessageIndex] = useState<number | null>(null);
  const [currentFeedbackMessage, setCurrentFeedbackMessage] = useState<ChatRequest | null>(null);
  const [isReceivingResponse, setIsReceivingResponse] = useState(false);

  useEffect(() => {
    if (!citationSelection) setSelectedCitation(null);
  }, [citationSelection]);

  // Notify parent when receiving response state changes (for disabling editor during generation)
  useEffect(() => {
    onReceivingResponseChange?.(isReceivingResponse);
  }, [isReceivingResponse, onReceivingResponseChange]);

  const { isAskPopoverOpen } = useActionPopoverContext();
  const [focusSignal, setFocusSignal] = useState(0);
  const previousPopoverOpenRef = useRef(false);
  const previousSelectedConversationIdRef = useRef<number | null>(selectedConversationId ?? null);

  const userContext = useContext(UserContext);
  const timezone = userContext.state.timezone || CurrentAppConfig.DefaultTimezone;

  const messageTextRef = useRef('');
  const isLetterCanvasOpenRef = useRef(isLetterCanvasOpen);
  useEffect(() => {
    isLetterCanvasOpenRef.current = isLetterCanvasOpen;
  }, [isLetterCanvasOpen]);

  const isNewConversationRef = useRef(false);
  const parsedPatientId = parseInt(patientId, 10);

  useQuery(USER_ROLE_QUERY);

  // Lazy query to check for saved letter when clicking a document button
  const [getSavedLetter] = useLazyQuery(AI_LETTER_QUERY, {
    fetchPolicy: 'network-only',
  });

  const {
    feedbackModalOpen,
    feedbackSnackbarOpen,
    feedbackLoading,
    feedbackSuccess,
    feedbackErrorMessage,
    openFeedbackModal,
    closeFeedbackModal,
    submitFeedback,
    closeFeedbackSnackbar,
  } = useFeedbackHandler({ defaultCommentPrefix: 'AI Chat feedback' });

  const { activeConversation, chatHistoryRef, updateConversation, getBaseConversation, setLocalConversation } =
    useConversationState({
      chatHistory,
      selectedConversationId,
      index,
      streamingAbortControllerRef,
      setInterruptedMessage,
    });

  const requests = activeConversation.requests || [];

  // Find the latest document in the conversation (only one per conversation).
  // Do NOT require letterHtml — after refresh it is intentionally absent from
  // GraphQL history, but the DB record may still exist and is fetched lazily.
  const latestDocumentRequest = [...requests].reverse().find((r) => isDocumentType(r.type) && !r.isEdit);
  const latestDocumentIndex = latestDocumentRequest ? requests.lastIndexOf(latestDocumentRequest) : -1;

  // Opens the latest saved letter from DB — mirrors the in-chat pill click.
  // Used by the sticky "Draft" button so it always shows persisted content.
  const handleOpenLatestLetter = useCallback(async () => {
    if (!latestDocumentRequest) return;
    logDatadogRumCustomAction('ai_chat_open_latest_letter', {
      patientId,
      viewMode: isPopoverLayout ? 'popover' : 'default',
      documentType: latestDocumentRequest.type,
      conversationId: activeConversation.conversationId ?? null,
    });
    const conversationId = activeConversation.conversationId;
    if (conversationId) {
      try {
        const { data } = await getSavedLetter({
          variables: {
            patientId: parsedPatientId,
            conversationId,
            letterType: latestDocumentRequest.type,
          },
        });
        if (data?.aiLetter?.contentHtml) {
          if (data.aiLetter.id && setAiLetterId) {
            setAiLetterId(data.aiLetter.id);
          }
          openLetterCanvas(
            data.aiLetter.contentHtml,
            conversationId,
            latestDocumentRequest.type,
            latestDocumentRequest.createdAt,
            false,
          );
          return;
        }
      } catch (error) {
        logger.error('fetchSavedLetterForStickyPill', 'Failed to fetch saved letter for sticky pill');
      }
    }
    // Fallback: no DB record yet — open from in-memory message HTML (new document, pre-refresh)
    if (latestDocumentRequest.letterHtml) {
      openLetterCanvas(
        latestDocumentRequest.letterHtml,
        conversationId,
        latestDocumentRequest.type,
        latestDocumentRequest.createdAt,
      );
    }
  }, [
    latestDocumentRequest,
    activeConversation.conversationId,
    getSavedLetter,
    parsedPatientId,
    setAiLetterId,
    openLetterCanvas,
    patientId,
    isPopoverLayout,
  ]);

  // Track whether the in-chat document pill is visible in the scroll area
  const documentPillRef = useRef<HTMLDivElement>(null);
  const [isDocumentPillVisible, setIsDocumentPillVisible] = useState(true);

  useEffect(() => {
    const el = documentPillRef.current;
    const scrollRoot = chatContentRef.current;
    if (!el || !scrollRoot) return;
    const observer = new IntersectionObserver(([entry]) => setIsDocumentPillVisible(entry.isIntersecting), {
      root: scrollRoot,
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [latestDocumentIndex]);

  // Show loading indicator whenever we're fetching conversation data
  // This provides immediate feedback when clicking on a conversation, even if there might be stale data
  // However, don't show loading if we already have local data for THIS specific conversation (from streaming)
  // This prevents reload flash when a new conversation finishes streaming and selectedConversationId is set
  const hasLocalDataForSelectedConversation =
    activeConversation.conversationId === selectedConversationId &&
    activeConversation.requests &&
    activeConversation.requests.length > 0;
  // Also show loading on the very first conversations-list fetch so we don't flash
  // the empty "new conversation" state before localStorage restores a selection.
  const showLoading =
    isInitialising ||
    (isLoadingConversation && selectedConversationId !== null && !hasLocalDataForSelectedConversation);
  // Centered empty state should only render when truly empty AND not loading.
  const showEmptyState = isInputCentered && !showLoading;
  const chatTitle = isDemo
    ? "How can I assist with this patient's care?"
    : 'What would you like to know about this patient?';

  useEffect(() => {
    setIsInputCentered(!requests.length);
  }, [requests.length]);

  useEffect(() => {
    if (!isPopoverLayout) return;
    if (isAskPopoverOpen && !previousPopoverOpenRef.current) {
      setFocusSignal((prev) => prev + 1);
    }
    previousPopoverOpenRef.current = isAskPopoverOpen;
  }, [isAskPopoverOpen, isPopoverLayout]);

  useEffect(() => {
    if (!isPopoverLayout || !isAskPopoverOpen) {
      previousSelectedConversationIdRef.current = selectedConversationId ?? null;
      return;
    }
    const previousId = previousSelectedConversationIdRef.current;
    if (previousId !== null && selectedConversationId === null) {
      setFocusSignal((prev) => prev + 1);
    }
    previousSelectedConversationIdRef.current = selectedConversationId ?? null;
  }, [isAskPopoverOpen, isPopoverLayout, selectedConversationId]);

  // Handle special citation types (document types use the canvas instead)
  useEffect(() => {
    const specialChat = requests.filter((r) => r.type in CITATION_GENERATORS && !isDocumentType(r.type)).pop();
    if (specialChat) {
      const citation = CITATION_GENERATORS[specialChat.type](patientId);
      setSelectedCitation(citation.id || null);
      setCitationSelection({ citations: [citation], activeIndex: 0 });
    }
  }, [requests, patientId, setCitationSelection]);

  const handleSubmit = useCallback(
    async (messageText: string) => {
      if (isReceivingResponse) return;
      // Determine conversation ID:
      // - If user explicitly selected a new conversation (null), always use undefined
      // - If user selected an existing conversation (number), use that
      // - Only use letterConversationId if selectedConversationId is undefined (not explicitly set)
      //   and we're continuing the same conversation (document is open)
      const currentId =
        selectedConversationId === null
          ? undefined
          : selectedConversationId !== undefined
          ? selectedConversationId
          : isLetterCanvasOpen && letterConversationId
          ? letterConversationId
          : undefined;

      logDatadogRumCustomAction('ai_chat_submit_message', {
        patientId,
        viewMode: isPopoverLayout ? 'popover' : 'default',
        conversationId: currentId,
      });

      const isNew = !currentId;
      isNewConversationRef.current = isNew;
      messageTextRef.current = messageText;

      // Get active document HTML for editing context (passed as separate parameter)
      // IMPORTANT: Only pass the document if it belongs to the CURRENT conversation
      // This prevents the orchestrator from treating a new conversation request as an edit
      // when a document from a different conversation is open
      const rawActiveDocumentHtml = getActiveDocumentHtml();
      const activeDocumentHtml =
        rawActiveDocumentHtml && letterConversationId === currentId ? rawActiveDocumentHtml : undefined;

      const { base, requests: existingRequests } = getBaseConversation();
      // Show original message text in UI, but send wrapped version to backend
      const newRequest = createRequest(messageText);
      const newRequestCreatedAt = newRequest.createdAt; // Store for use in updateRequest calls

      // Build request list - clean up any incomplete requests (no response) and add the new one
      // Allow duplicate questions - each is uniquely identified by text + createdAt
      const cleanedRequests = existingRequests.filter((r) => r.response);
      const finalRequests = [...cleanedRequests, newRequest];

      const title = isNew ? messageText.substring(0, 255) : base.title || 'New Conversation';
      const initialState: ChatHistoryItem = {
        ...base,
        conversationId: currentId,
        title,
        requests: finalRequests,
        kind: currentId ? 'conversation' : 'new',
      };

      setLocalConversation(initialState);
      chatHistoryRef.current = initialState;
      setIsInputCentered(false);
      setIsReceivingResponse(true);

      if (interruptedMessage?.conversationId === currentId) setInterruptedMessage(null);

      const abortController = new AbortController();
      streamingAbortControllerRef.current = abortController;

      const handleError = (error: Error) => {
        const isCancelled = error.message === 'Streaming cancelled' || error.name === 'AbortError';

        // 🔹 Case 1: Cancelled NEW conversation → only touch localConversation, never chatHistory
        if (isCancelled && currentId === undefined) {
          const current = chatHistoryRef.current;
          // Only clean within the new-conversation draft - match by createdAt to handle duplicates
          const cleanedRequests = (current.requests || []).filter(
            (r) => !(r.text === messageText && r.createdAt === newRequestCreatedAt && !r.response),
          );
          // If nothing left, drop the local conversation entirely
          if (!cleanedRequests.length) {
            setLocalConversation(null);
          } else {
            setLocalConversation({ ...current, requests: cleanedRequests });
          }
          // No interruptedMessage and no chatHistory update for a new conversation
          return;
        }

        // Case 2: Cancelled EXISTING conversation → use chatHistoryRef + interruptedMessage
        const current = chatHistoryRef.current;

        if (isCancelled) {
          const cleaned = {
            ...current,
            requests: current.requests.filter(
              (r) => !(r.text === messageText && r.createdAt === newRequestCreatedAt && !r.response),
            ),
          };
          setLocalConversation(cleaned);
          chatHistoryRef.current = cleaned;
          if (currentId !== undefined) {
            setInterruptedMessage({ text: messageText, conversationId: currentId });
          }
          return;
        }

        // Case 3: Real error → show error in the appropriate conversation
        const updated = updateRequest(
          current.requests,
          messageText,
          {
            response: `Error: ${error.message}`,
            responseCreatedAt: new Date().toISOString(), // Set timestamp to clear loading state
          },
          newRequestCreatedAt,
        );
        // Use updateConversation to update both localConversation (UI) and chatHistoryRef (state)
        updateConversation({ requests: updated, conversationId: current.conversationId || currentId });
        // Also update chatHistory for existing conversations
        if (current.conversationId) {
          setChatHistory({ ...current, requests: updated }, index);
        }
      };

      try {
        await streamChatResponse(
          messageText,
          parsedPatientId,
          currentId,
          {
            onConversationId: (conversationId) => {
              // 🔒 Safety: ignore if this request has been cancelled
              if (abortController.signal.aborted) return;

              const updated = updateConversation({
                conversationId,
                title: isNewConversationRef.current
                  ? messageTextRef.current.substring(0, 255)
                  : chatHistoryRef.current.title,
                kind: 'conversation', // Mark as conversation so it displays the title instead of "New conversation"
              });
              setChatHistory(updated, index);
            },
            onStatus: (statusText) => {
              if (abortController.signal.aborted) return;

              const current = chatHistoryRef.current;
              const updated = updateRequest(current.requests, messageText, { statusText }, newRequestCreatedAt);
              updateConversation({ requests: updated, conversationId: current.conversationId || currentId });
            },
            onReasoning: (reasoningText) => {
              if (abortController.signal.aborted) return;

              const current = chatHistoryRef.current;
              const updated = updateRequest(current.requests, messageText, { reasoningText }, newRequestCreatedAt);
              updateConversation({ requests: updated, conversationId: current.conversationId || currentId });
            },
            onReasoningEnd: () => {
              if (abortController.signal.aborted) return;

              const current = chatHistoryRef.current;
              const updated = updateRequest(
                current.requests,
                messageText,
                { reasoningText: undefined },
                newRequestCreatedAt,
              );
              updateConversation({ requests: updated, conversationId: current.conversationId || currentId });
            },
            onCitations: (citations) => {
              if (abortController.signal.aborted) return;

              // Render citation chips as soon as the backend yields them, instead of
              // waiting for the trailing `done` event. Cuts perceived latency when
              // the response text has already streamed in full.
              const current = chatHistoryRef.current;
              const updated = updateRequest(current.requests, messageText, { citations }, newRequestCreatedAt);
              updateConversation({ requests: updated, conversationId: current.conversationId || currentId });
              // Mirror onChunk: persist into chatHistory so chips survive a sidebar
              // switch-away-and-back before `done` lands.
              if (current.conversationId) {
                setChatHistory({ ...current, requests: updated }, index);
              }
            },
            onChunk: (response) => {
              if (abortController.signal.aborted) return; // avoid updating after cancel

              // This callback is already called from requestAnimationFrame, so updates are naturally batched
              // updateConversation handles both localConversation (for UI) and chatHistoryRef (for state)
              // Clear statusText when we start receiving actual content
              const current = chatHistoryRef.current;
              const updated = updateRequest(
                current.requests,
                messageText,
                { response, statusText: undefined },
                newRequestCreatedAt,
              );
              updateConversation({ requests: updated, conversationId: current.conversationId || currentId });

              // For existing conversations, also update chatHistory to persist state
              if (current.conversationId) {
                setChatHistory({ ...current, requests: updated }, index);
              }
              // For new conversations, updateConversation already updates localConversation which drives the UI
            },
            onComplete: ({ conversationId, title: newTitle, citations, type, letterHtml, isEdit, finalText }) => {
              if (abortController.signal.aborted) return; // don't finalize cancelled runs

              const current = chatHistoryRef.current;
              const streamedResponse = current.requests.find(
                (r) => r.text === messageText && r.createdAt === newRequestCreatedAt,
              )?.response;
              // Prefer the server's post-processed `finalText` (markdown→HTML, citation
              // refs stripped). Use `??` so an empty string from the server — a
              // legitimate "response is empty" signal — is preserved, instead of
              // falling back to a streamed buffer that may still contain raw markdown.
              const responseText = finalText ?? streamedResponse ?? '';

              const updated = updateRequest(
                current.requests,
                messageText,
                {
                  response: responseText,
                  citations,
                  responseCreatedAt: new Date().toISOString(),
                  type: type || '',
                  letterHtml: letterHtml || undefined,
                  isEdit: isEdit || undefined,
                },
                newRequestCreatedAt,
              );

              // Only use newTitle for new conversations (isNewConversationRef.current)
              // For follow-up messages, preserve the existing title to respect user renames
              const titleToUse = isNewConversationRef.current ? newTitle || current.title : current.title;

              const completed = updateConversation({
                title: titleToUse,
                conversationId: conversationId || current.conversationId,
                requests: updated,
                kind: conversationId || current.conversationId ? 'conversation' : current.kind, // Ensure kind is set for conversations
              });

              if (conversationId) {
                // Update local state optimistically - this will add new conversations to "Today" section
                // via handleChatHistoryChange without triggering a full reload
                setChatHistory(completed, index);

                // For new conversations, skip immediate refetch to prevent reload on full page.
                // Exception: in popover layout, always refetch so the conversation list stays in sync
                // when the user navigates away and back without a page reload.
                // For existing conversations, refetch immediately to sync with server.
                if (!isNewConversationRef.current || isPopoverLayout) {
                  refetchConversations?.();
                }

                if (
                  isNewConversationRef.current &&
                  (newTitle === messageText.substring(0, 255) || newTitle === messageText) &&
                  setPendingTitleUpdateConversationId
                ) {
                  setPendingTitleUpdateConversationId(conversationId);
                }
              }

              // If response is a document type, update or open the letter canvas with the document HTML
              if (type && isDocumentType(type) && letterHtml) {
                // For edits, update the existing canvas content
                // For new documents, open the canvas and auto-create AI Letter
                if (isEdit && isLetterCanvasOpenRef.current) {
                  // Update the existing letter canvas content with the edited document
                  updateLetterCanvas(letterHtml);
                } else {
                  // Pass newRequestCreatedAt so save will update this specific message's letterHtml
                  // Pass isNewDocument=true to auto-create AI Letter
                  openLetterCanvas(
                    letterHtml,
                    conversationId || current.conversationId,
                    type,
                    newRequestCreatedAt,
                    !isEdit,
                  );
                }
              }
            },
            onError: handleError,
          },
          abortController,
          activeDocumentHtml,
        );
      } catch (error: any) {
        handleError(error);
      } finally {
        streamingAbortControllerRef.current = null;
        setIsReceivingResponse(false);
      }
    },
    [
      isReceivingResponse,
      selectedConversationId,
      getBaseConversation,
      setLocalConversation,
      chatHistoryRef,
      updateConversation,
      setChatHistory,
      index,
      patientId,
      refetchConversations,
      setPendingTitleUpdateConversationId,
      interruptedMessage,
      setInterruptedMessage,
      streamingAbortControllerRef,
      getActiveDocumentHtml,
      openLetterCanvas,
      isLetterCanvasOpen,
      letterConversationId,
      isPopoverLayout,
    ],
  );

  const handleRetry = useCallback(() => {
    // Only retry for existing conversations (with an ID)
    if (!interruptedMessage || interruptedMessage.conversationId === undefined) return;
    logDatadogRumCustomAction('ai_chat_retry_message', {
      patientId,
      viewMode: isPopoverLayout ? 'popover' : 'default',
      conversationId: interruptedMessage.conversationId,
    });
    if (interruptedMessage.conversationId === activeConversation.conversationId) {
      handleSubmit(interruptedMessage.text);
      setInterruptedMessage(null);
    }
  }, [interruptedMessage, activeConversation, handleSubmit, setInterruptedMessage, patientId, isPopoverLayout]);

  // Only show retry banner for existing conversations (with an ID)
  // Also ensure it matches selectedConversationId to prevent brief flashes when switching
  const showRetryBanner =
    interruptedMessage &&
    interruptedMessage.conversationId !== undefined &&
    interruptedMessage.conversationId === activeConversation.conversationId &&
    interruptedMessage.conversationId === selectedConversationId;

  const handleCitationClick = useCallback(
    (citation: CitationProps, citations: CitationProps[], citationIndex: number) => {
      logDatadogRumCustomAction('ai_chat_citation_open', {
        patientId,
        viewMode: isPopoverLayout ? 'popover' : 'default',
        documentType: citation.documentType,
      });
      setSelectedCitation(citation.id || null);
      setCitationSelection({ citations, activeIndex: citationIndex });
    },
    [setCitationSelection, patientId, isPopoverLayout],
  );

  const handleEditClick = useCallback(
    async (type: string, message: ChatRequest) => {
      logDatadogRumCustomAction('ai_chat_edit', {
        patientId,
        viewMode: isPopoverLayout ? 'popover' : 'default',
        conversationId: activeConversation.conversationId ?? null,
      });

      if (isDocumentType(type)) {
        // Only allow document generation in Ask chat (not popover) and when feature is enabled
        if (!hasDocumentGenerationFeature || isPopoverLayout) {
          return;
        }

        // First check if there's a saved version of this letter in the AiLetter table
        if (activeConversation.conversationId) {
          try {
            const { data } = await getSavedLetter({
              variables: {
                patientId: parsedPatientId,
                conversationId: activeConversation.conversationId,
                letterType: type,
              },
            });

            // If a saved letter exists, use that content (it's the latest saved version)
            if (data?.aiLetter?.contentHtml) {
              if (data.aiLetter.id && setAiLetterId) {
                setAiLetterId(data.aiLetter.id);
              }
              // Pass isNewDocument=false since the letter is already persisted.
              // openLetterCanvas will sync all refs with the latest content.
              openLetterCanvas(
                data.aiLetter.contentHtml,
                activeConversation.conversationId,
                type,
                message.createdAt,
                false,
              );
              return;
            }
          } catch (error) {
            // If query fails, fall back to message content below
            logger.error('getSavedLetter', 'Failed to get saved letter');
          }
        }

        // Fall back to message's letterHtml content (original generated version)
        const letterContent = message.letterHtml || message.response;
        if (!letterContent) {
          return;
        }
        openLetterCanvas(letterContent, activeConversation.conversationId, type, message.createdAt);
      } else if (type in CITATION_GENERATORS) {
        const citation = CITATION_GENERATORS[type](patientId);
        setSelectedCitation(citation.id || null);
        setCitationSelection({ citations: [citation], activeIndex: 0 });
      }
    },
    [
      patientId,
      setCitationSelection,
      openLetterCanvas,
      activeConversation.conversationId,
      getSavedLetter,
      parsedPatientId,
      hasDocumentGenerationFeature,
      isPopoverLayout,
      setAiLetterId,
    ],
  );

  const handleFeedbackClick = useCallback(
    (msg: ChatRequest) => {
      logDatadogRumCustomAction('ai_chat_feedback_open', {
        patientId,
        viewMode: isPopoverLayout ? 'popover' : 'default',
        conversationId: activeConversation.conversationId ?? null,
      });
      setCurrentFeedbackMessage(msg);
      openFeedbackModal();
    },
    [openFeedbackModal, patientId, isPopoverLayout, activeConversation.conversationId],
  );

  // Memoize the hover handler to prevent unnecessary re-renders
  const handleMessageHover = useCallback((index: number | null) => {
    setHoveredMessageIndex(index);
  }, []);

  const { chatContentRef, isScrolled, handleScroll } = useChatAutoScroll({
    requestsLength: requests.length,
    requests,
    isReceivingResponse,
    onScrollStateChange,
  });

  return (
    <ChatPanelContainer>
      {!isPopoverLayout && (
        <>
          <ChatHeader title="Ask" isScrolled={isScrolled} />
          <PreCutoffDisclaimer patientId={patientId} />
        </>
      )}
      <Box
        sx={{
          height: '0',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          justifyContent: isPopoverLayout && showEmptyState ? 'flex-end' : 'flex-start',
        }}>
        <ChatContent isInitial={showEmptyState} ref={chatContentRef} onScroll={handleScroll}>
          <Box sx={{ maxWidth: '786px', margin: '0 auto', width: '100%' }}>
            <ChatBox isHidden={showEmptyState}>
              {showLoading ? (
                <Stack direction="column" gap={2}>
                  <Stack direction="column" alignItems="flex-end">
                    <Skeleton variant="text" width="80%" height={32} animation="wave" />
                    <Skeleton variant="text" width="40%" height={24} animation="wave" />
                  </Stack>
                  <Stack direction="column" alignItems="flex-start">
                    <Skeleton variant="text" width="80%" height={32} animation="wave" />
                    <Skeleton variant="text" width="40%" height={24} animation="wave" />
                  </Stack>
                </Stack>
              ) : (
                <>
                  {requests.map((message, idx) => {
                    const chatMsg = (
                      <ChatMessage
                        key={idx}
                        message={message}
                        index={idx}
                        timezone={timezone}
                        hoveredMessageIndex={hoveredMessageIndex}
                        selectedCitation={selectedCitation}
                        isPopoverLayout={isPopoverLayout}
                        onMessageHover={handleMessageHover}
                        onCitationClick={handleCitationClick}
                        onFeedbackClick={handleFeedbackClick}
                        onEditClick={handleEditClick}
                        hasDocumentGenerationFeature={hasDocumentGenerationFeature}
                      />
                    );
                    if (idx === latestDocumentIndex) {
                      return (
                        <div ref={documentPillRef} key={idx}>
                          {chatMsg}
                        </div>
                      );
                    }
                    return chatMsg;
                  })}
                  {showRetryBanner && (
                    <Alert
                      icon={<ErrorOutlineIcon fontSize="inherit" />}
                      severity="error"
                      variant="outlined"
                      action={
                        <Button onClick={handleRetry} variant="text" sx={{ color: theme.palette.error.dark }}>
                          RETRY
                        </Button>
                      }>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ fontWeight: 500, color: theme.palette.error.dark }}>
                        Generation was interrupted. Would you like to retry?
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </ChatBox>
          </Box>
        </ChatContent>

        <ChatInputContainer isPopoverLayout={isPopoverLayout} isInitial={showEmptyState}>
          {!isPopoverLayout &&
            hasDocumentGenerationFeature &&
            latestDocumentRequest &&
            !showEmptyState &&
            !isDocumentPillVisible && (
              <Box sx={{ width: '100%', maxWidth: '786px', pb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="document-pill-button"
                  startIcon={<DescriptionIcon sx={{ color: 'primary.main' }} />}
                  onClick={handleOpenLatestLetter}
                  sx={{
                    width: 'fit-content',
                    borderRadius: '4px',
                    color: 'primary.main',
                    textTransform: 'none',
                  }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    {`Draft: ${getDocumentDisplayName(latestDocumentRequest.type)}`}
                  </Typography>
                </Button>
              </Box>
            )}
          {showEmptyState && (
            <Typography
              variant={isPopoverLayout ? 'subtitle1' : 'h6'}
              fontWeight={500}
              textAlign={isPopoverLayout ? 'left' : 'center'}
              sx={{ paddingBottom: '16px', marginBottom: isPopoverLayout ? '0px' : '8px' }}>
              {chatTitle}
            </Typography>
          )}
          {!isInitialising && (
            <AiAskBar
              onInputChange={handleSubmit}
              hideSuggestions={!showEmptyState}
              layout={isPopoverLayout ? 'popover' : 'default'}
              isSubmitDisabled={isReceivingResponse}
              autoFocus={isPopoverLayout ? isAskPopoverOpen : showEmptyState}
              focusSignal={isPopoverLayout ? focusSignal : undefined}
              patientId={patientId}
              conversationId={selectedConversationId ?? null}
            />
          )}
        </ChatInputContainer>
      </Box>

      <NotificationSnackbar
        open={feedbackSnackbarOpen}
        onClose={closeFeedbackSnackbar}
        type={feedbackSuccess ? 'feedback' : 'error'}
        message={feedbackSuccess ? undefined : feedbackErrorMessage}
      />
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => {
          setCurrentFeedbackMessage(null);
          closeFeedbackModal();
        }}
        onSubmit={(category, comments) =>
          currentFeedbackMessage &&
          submitFeedback(category, comments, {
            text: currentFeedbackMessage.text,
            response: currentFeedbackMessage.response,
            type: currentFeedbackMessage.type,
          }).then(() => {
            logDatadogRumCustomAction('ai_chat_feedback_submit', {
              patientId,
              category,
              viewMode: isPopoverLayout ? 'popover' : 'default',
              key: currentFeedbackMessage.type,
              conversationId: activeConversation.conversationId ?? null,
              value: comments.length > 0 ? 'has_comments' : 'no_comments',
            });
            setCurrentFeedbackMessage(null);
          })
        }
        loading={feedbackLoading}
      />
    </ChatPanelContainer>
  );
};

// ============================================================================
// ChatContainer (root)
// ============================================================================
interface ChatContainerProps {
  patientId: string;
  isPopoverLayout?: boolean;
  onSelectedConversationIdChange?: (conversationId: number | null) => void;
  onScrollStateChange?: (isScrolled: boolean) => void;
  onConversationSelected?: () => void;
  renderHistoryWrapper?: (list: React.ReactNode) => React.ReactNode;
  startNewConversationRef?: React.MutableRefObject<(() => void) | null>;
}

const ChatContainer = ({
  patientId,
  isPopoverLayout = false,
  onSelectedConversationIdChange,
  onScrollStateChange,
  onConversationSelected,
  renderHistoryWrapper,
  startNewConversationRef,
}: ChatContainerProps): JSX.Element => {
  const [citationSelection, setCitationSelection] = useState<CitationSelection | null>(null);
  const { setIsSideNavbarCollapsed } = useContext(NavigationContext);
  const parsedPatientId = parseInt(patientId, 10);
  const location = useLocation();
  // Letter/document canvas state
  const [letterHtml, setLetterHtml] = useState<string | null>(null);
  const [letterDirty, setLetterDirty] = useState(false);
  const [letterConversationId, setLetterConversationId] = useState<number | null>(null);
  const [letterType, setLetterType] = useState<string>('hpi_document');
  const [letterSaving, setLetterSaving] = useState(false);
  const [letterMessageCreatedAt, setLetterMessageCreatedAt] = useState<string | null>(null);
  const [aiLetterId, setAiLetterId] = useState<number | null>(null);
  // Track if AI is currently generating/editing to disable the editor
  const [isReceivingResponse, setIsReceivingResponse] = useState(false);

  // Use refs to always have latest values in callbacks (avoids stale closures)
  const letterHtmlRef = useRef<string | null>(null);
  const letterConversationIdRef = useRef<number | null>(null);
  const letterTypeRef = useRef<string>('hpi_document');
  const letterMessageCreatedAtRef = useRef<string | null>(null);
  const aiLetterIdRef = useRef<number | null>(null);
  // Ref-based dirty flag updated immediately (letterDirty state is debounced for UI only).
  // Used by navigate-away and close saves to avoid missing unsaved changes.
  const letterDirtyRef = useRef<boolean>(false);

  // Callback to update message letterHtml after save (passed from ChatHistory)
  const onLetterSavedRef = useRef<((conversationId: number, messageCreatedAt: string, newHtml: string) => void) | null>(
    null,
  );

  const setOnLetterSaved = useCallback(
    (callback: (conversationId: number, messageCreatedAt: string, newHtml: string) => void) => {
      onLetterSavedRef.current = callback;
    },
    [],
  );

  // Keep refs in sync with state
  useEffect(() => {
    letterHtmlRef.current = letterHtml;
  }, [letterHtml]);

  useEffect(() => {
    letterConversationIdRef.current = letterConversationId;
  }, [letterConversationId]);

  useEffect(() => {
    letterTypeRef.current = letterType;
  }, [letterType]);

  useEffect(() => {
    letterMessageCreatedAtRef.current = letterMessageCreatedAt;
  }, [letterMessageCreatedAt]);

  useEffect(() => {
    aiLetterIdRef.current = aiLetterId;
  }, [aiLetterId]);

  const [upsertAiLetter] = useMutation(UPSERT_AI_LETTER_MUTATION, {
    // Handle errors locally to prevent Apollo's global error handler from triggering redirect
    errorPolicy: 'all',
    context: {
      // Tell Apollo's global error handler to ignore these errors so we can handle them locally
      locallyHandledErrors: [
        'Conversation does not belong to the specified patient',
        'Document generation feature is not enabled',
        'No letter content provided or found in conversation',
      ],
    },
    onError: (error) => {
      logger.error('upsertAiLetter', 'Failed to upsert AI letter');
    },
  });
  const [sendAiLetterToFhir] = useMutation(SEND_AI_LETTER_TO_FHIR_MUTATION, {
    // Handle errors locally to prevent Apollo's global error handler from triggering redirect
    errorPolicy: 'all',
    context: {
      locallyHandledErrors: [
        'Document generation feature is not enabled',
        'AI letter not found',
        'Failed to send AI letter to FHIR',
      ],
    },
    onError: (error) => {
      logger.error('sendAiLetterToFhir', 'Failed to send AI letter to FHIR');
    },
  });

  // Debounce timer for letter state updates — avoids re-rendering ChatPanel (and
  // consequently re-rendering EditLetterPanel) on every single keystroke, which was
  // causing the TipTap editor to scroll to the bottom mid-typing.
  const letterChangeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLetterChange = useCallback((content: string) => {
    // Update refs immediately so saves always use the latest HTML and dirty state.
    letterHtmlRef.current = content;
    letterDirtyRef.current = true;

    // Only trigger a state update (and the resulting re-render cascade) once the
    // user pauses for 300 ms. This prevents scroll-jump on every keystroke while
    // still keeping letterDirty / letterHtml state eventually consistent.
    if (letterChangeDebounceRef.current) {
      clearTimeout(letterChangeDebounceRef.current);
    }
    letterChangeDebounceRef.current = setTimeout(() => {
      setLetterHtml(content);
      setLetterDirty(true);
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (letterChangeDebounceRef.current) {
        clearTimeout(letterChangeDebounceRef.current);
      }
    };
  }, []);

  const apolloClient = useApolloClient();

  const handleLetterSave = useCallback(
    async (isAutoSave: boolean = false): Promise<number | null> => {
      const currentHtml = letterHtmlRef.current;
      const currentConversationId = letterConversationIdRef.current;
      const currentLetterType = letterTypeRef.current;
      const currentMessageCreatedAt = letterMessageCreatedAtRef.current;
      const currentAiLetterId = aiLetterIdRef.current;

      if (!currentHtml) return null;

      const isActuallyAutoSave = isAutoSave;
      const isInitialSave = currentAiLetterId === null;

      setLetterSaving(true);
      try {
        let savedId: number | null = null;

        if (isInitialSave && currentConversationId && currentMessageCreatedAt) {
          // Initial save: create the AiLetter record via GraphQL using messageCreatedAt
          // to resolve HTML from ChatMessage.response_data (avoids large HTML over GraphQL).
          const { data } = await upsertAiLetter({
            variables: {
              input: {
                patientId: parsedPatientId,
                conversationId: currentConversationId,
                letterType: currentLetterType,
                isSaved: !isActuallyAutoSave,
                messageCreatedAt: currentMessageCreatedAt,
              },
            },
          });
          savedId = data?.upsertAiLetter?.id ?? null;
          if (savedId) {
            setAiLetterId(savedId);
            aiLetterIdRef.current = savedId;
          }
          // GraphQL resolves HTML from ChatMessage (the original unedited version).
          // Always follow up with a REST call to write the current in-memory HTML so
          // any edits made before the first save are not lost.
        }

        // Always persist the current in-memory HTML via REST once we have an ID.
        // This covers: (a) initial save — overwrite the GraphQL-seeded HTML with the
        // real editor content, (b) subsequent edits — update the existing record.
        const letterId = savedId ?? currentAiLetterId ?? aiLetterIdRef.current;
        if (!letterId) {
          logger.error('handleLetterSave', 'No letter ID found');
          return null;
        }

        const encodedHtml = btoa(String.fromCharCode(...new TextEncoder().encode(currentHtml)));
        const response = await fetch('/server/ai/letter/update-content', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ai_letter_id: letterId,
            content_html_b64: encodedHtml,
            is_saved: !isActuallyAutoSave,
          }),
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Failed to save letter (${response.status}): ${text}`);
        }

        const result = await response.json();
        savedId = result?.id ?? letterId;

        if (!isActuallyAutoSave) {
          setLetterDirty(false);
          letterDirtyRef.current = false;
        }

        // Update the message's letterHtml in chat history for optimistic UI update
        if (currentConversationId && currentMessageCreatedAt && onLetterSavedRef.current) {
          onLetterSavedRef.current(currentConversationId, currentMessageCreatedAt, currentHtml);
        }

        // Refetch the letter query so updatedAt is fresh for next open
        apolloClient.refetchQueries({ include: [AI_LETTER_QUERY] }).catch(() => {});

        return savedId;
      } catch (error) {
        logger.error('handleLetterSave', 'Failed to save letter');
        return null;
      } finally {
        setLetterSaving(false);
      }
    },
    [parsedPatientId, upsertAiLetter, apolloClient],
  );

  const handleSendToFhir = useCallback(async () => {
    let id = aiLetterIdRef.current;
    if (!id) {
      id = await handleLetterSave();
    }
    if (!id) return;

    await sendAiLetterToFhir({
      variables: { input: { aiLetterId: id } },
    });
  }, [sendAiLetterToFhir, handleLetterSave]);

  const handleCitationSelection = useCallback(
    (selection: CitationSelection | null) => {
      if (!isPopoverLayout) {
        selection ? setIsSideNavbarCollapsed(true) : setIsSideNavbarCollapsed(false);
      }
      setCitationSelection(selection);
    },
    [isPopoverLayout, setIsSideNavbarCollapsed],
  );

  // Fetch AI config to check if document generation feature is enabled
  const { data: aiConfigData } = useQuery<AiConfigQueryData>(AI_CONFIG_QUERY);
  const hasDocumentGenerationFeature = aiConfigData?.aiConfig?.hasDocumentGenerationFeature ?? false;

  // Open the letter canvas with content from a chat response
  const openLetterCanvas = useCallback(
    async (
      content: string,
      conversationId?: number,
      docType?: string,
      messageCreatedAt?: string,
      isNewDocument: boolean = true,
    ) => {
      // Only allow document generation in Ask chat (not popover) and when feature is enabled
      if (!hasDocumentGenerationFeature || isPopoverLayout) {
        return;
      }

      const documentType = docType || 'hpi_document';
      setLetterHtml(content);
      setLetterDirty(false);
      letterDirtyRef.current = false;
      setLetterConversationId(conversationId || null);
      setLetterType(documentType);
      setLetterMessageCreatedAt(messageCreatedAt || null);

      // Always sync refs so subsequent saves use the correct content regardless of isNewDocument
      letterHtmlRef.current = content;
      letterConversationIdRef.current = conversationId || null;
      letterTypeRef.current = documentType;
      letterMessageCreatedAtRef.current = messageCreatedAt || null;

      // Clear any stale letter ID before saving so handleLetterSave creates a new record
      // rather than overwriting the previously generated letter.
      if (isNewDocument) {
        setAiLetterId(null);
        aiLetterIdRef.current = null;
      }

      // Save first, then display. Ensures the letter is persisted before the panel
      // opens and avoids querying for a letter that doesn't exist yet.
      let savedLetterId: number | null = null;
      if (isNewDocument && content) {
        try {
          savedLetterId = await handleLetterSave(true);
          if (savedLetterId) {
            setAiLetterId(savedLetterId);
            aiLetterIdRef.current = savedLetterId;
          }
        } catch (error) {
          logger.error('openLetterCanvas', 'Failed to auto-create AI letter');
        }
      }

      // Get display name for the document type
      const displayName = getDocumentDisplayName(documentType);
      const titleSuffix = documentType === 'hpi_document' ? 'RO-Consult' : displayName;

      // Open the letter panel with the generated content
      const letterCitation: CitationProps = {
        documentType: 'letter',
        title: titleSuffix,
        letterContent: content,
        onLetterChange: handleLetterChange,
        onLetterSave: handleLetterSave,
        letterSaving,
        letterDirty: false,
        patientId: parsedPatientId,
        conversationId,
        aiLetterId: savedLetterId || aiLetterId || undefined,
        onSendToFhir: handleSendToFhir,
        isEditorDisabled: isReceivingResponse,
      };
      handleCitationSelection({ citations: [letterCitation], activeIndex: 0 });
    },
    [
      handleCitationSelection,
      handleLetterChange,
      handleLetterSave,
      handleSendToFhir,
      letterSaving,
      parsedPatientId,
      aiLetterId,
      hasDocumentGenerationFeature,
      isPopoverLayout,
      isReceivingResponse,
    ],
  );

  // Get citation details if one is currently selected
  const citationSelectionActiveIndex = citationSelection?.activeIndex ?? 0;
  const citationSelectionItems = citationSelection?.citations ?? [];
  const citationContent = citationSelectionItems[citationSelectionActiveIndex] || null;
  const isLetterCanvasOpen = citationContent?.documentType === 'letter';

  // Query for HPI document - only when letter panel is open AND we have an aiLetterId
  // (i.e. letter was persisted). This avoids querying before the letter exists in the DB,
  // which could return null/errors and contribute to crashes on review environments.
  // network-only: never serve stale cache — the editor already holds the latest in-memory
  // content, so we only need this query to restore state after a full page refresh.
  const { data: aiLetterData } = useQuery(AI_LETTER_QUERY, {
    variables: {
      patientId: parsedPatientId,
      conversationId: isLetterCanvasOpen ? citationContent?.conversationId || letterConversationId || null : null,
      letterType: letterType || 'hpi_document',
    },
    skip: !parsedPatientId || !isLetterCanvasOpen || !aiLetterId,
    fetchPolicy: 'network-only',
  });

  // Restore letter content from DB after a full page refresh.
  // Only applies when letterHtml is null (i.e. in-memory state was lost on refresh) —
  // never overwrites content the user is actively editing or that was set by the AI stream.
  useEffect(() => {
    const fetchedHtml = aiLetterData?.aiLetter?.contentHtml;
    if (!fetchedHtml) return;
    // letterHtml is non-null when the AI stream already populated it this session.
    // In that case we trust in-memory over DB to avoid overwriting unsaved edits.
    if (letterHtmlRef.current !== null) return;
    setLetterHtml(fetchedHtml);
    letterHtmlRef.current = fetchedHtml;
  }, [aiLetterData]);

  // Save letter when navigating to another menu/route.
  // Uses letterDirtyRef (updated immediately) rather than letterDirty state (debounced)
  // so changes made right before navigation are never missed.
  const prevLocationRef = useRef(location.pathname);
  useEffect(() => {
    if (prevLocationRef.current !== location.pathname) {
      if (letterDirtyRef.current && aiLetterIdRef.current) {
        handleLetterSave(true);
      }
      prevLocationRef.current = location.pathname;
    }
  }, [location.pathname, handleLetterSave]);

  // Save letter on page refresh / tab close via sendBeacon for reliability
  const letterSaveBeaconRef = useRef<() => void>(() => {});
  useEffect(() => {
    letterSaveBeaconRef.current = () => {
      const html = letterHtmlRef.current;
      const letterId = aiLetterIdRef.current;
      if (!html || !letterId) return;

      const encodedHtml = btoa(String.fromCharCode(...new TextEncoder().encode(html)));
      const payload = JSON.stringify({
        ai_letter_id: letterId,
        content_html_b64: encodedHtml,
        is_saved: false,
      });
      navigator.sendBeacon('/server/ai/letter/update-content', new Blob([payload], { type: 'application/json' }));
    };
  });
  useEffect(() => {
    const onBeforeUnload = () => {
      letterSaveBeaconRef.current();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Determine which citation panel to show
  const showCitationPanel = Boolean(citationSelection);
  const showSideCitationPanel = showCitationPanel && !isPopoverLayout;
  const showOverlayCitationPanel = showCitationPanel && isPopoverLayout;

  // Update citation active
  const handleActiveIndexChange = useCallback((nextIndex: number) => {
    setCitationSelection((prev) => (prev ? { ...prev, activeIndex: nextIndex } : prev));
  }, []);

  // Update the letter canvas content (for edits) without reopening
  const updateLetterCanvas = useCallback(
    (content: string) => {
      setLetterHtml(content);
      setLetterDirty(true);
      // Update refs immediately for prompt wrapping and dirty tracking
      letterHtmlRef.current = content;
      letterDirtyRef.current = true;

      // Also update the citationContent to trigger editor re-render
      // Use functional form to get latest citationSelection state
      setCitationSelection((prev) => {
        if (!prev) return null;
        const currentCitation = prev.citations[prev.activeIndex];
        if (currentCitation?.documentType === 'letter') {
          const updatedCitation: CitationProps = {
            ...currentCitation,
            letterContent: content,
          };
          return {
            ...prev,
            citations: prev.citations.map((c, i) => (i === prev.activeIndex ? updatedCitation : c)),
          };
        }
        return prev;
      });

      // Persist the AI edit immediately so it survives page refresh
      handleLetterSave(true).catch((err) => {
        logger.error('onEditComplete', 'Failed to auto-save after AI edit');
      });
    },
    [setCitationSelection, handleLetterSave],
  );

  // Update letter canvas when letter state changes (only if canvas is open)
  // Use a ref to track if we're actively showing the letter to avoid re-opening closed panels
  useEffect(() => {
    // Remove citationContent from dependencies
    // Derive it inside the functional updater instead
    setCitationSelection((prev) => {
      if (!prev) return null;

      const currentCitation = prev.citations[prev.activeIndex];

      // Only update if it's a letter and we have letterHtml
      if (letterHtml !== null && currentCitation?.documentType === 'letter') {
        const updatedCitation: CitationProps = {
          ...currentCitation,
          letterContent: letterHtml,
          onLetterChange: handleLetterChange,
          onLetterSave: handleLetterSave,
          letterSaving,
          letterDirty,
          aiLetterId: aiLetterId || undefined,
          onSendToFhir: handleSendToFhir,
          isEditorDisabled: isReceivingResponse,
        };

        return {
          ...prev,
          citations: prev.citations.map((c, i) => (i === prev.activeIndex ? updatedCitation : c)),
        };
      }

      return prev; // ← No change if not a letter
    });
  }, [
    letterHtml,
    letterDirty,
    letterSaving,
    aiLetterId,
    isReceivingResponse,
    // citationContent removed ✓
    handleLetterChange,
    handleLetterSave,
    handleSendToFhir,
  ]);

  const closeLetterCanvas = useCallback(() => {
    handleCitationSelection(null);
    // Keep letterHtml in state so we can wrap prompts until explicitly cleared
  }, [handleCitationSelection]);

  // Get active document HTML for editing context (if a letter is open)
  // The orchestrator will decide if user wants to edit the document or just ask a question
  const getActiveDocumentHtml = useCallback((): string | undefined => {
    const citationContent = citationSelection?.citations?.[citationSelection.activeIndex];
    if (letterHtmlRef.current && citationContent?.documentType === 'letter') {
      return letterHtmlRef.current;
    }
    return undefined;
  }, [citationSelection]);

  return (
    <>
      {/* Navigation warning dialog intentionally disabled — letter saves automatically on close */}
      <Grid
        container
        sx={{
          padding: 0,
          height: isPopoverLayout ? '100%' : undefined,
          minHeight: isPopoverLayout ? 0 : undefined,
        }}>
        <Grid
          item
          xs={showSideCitationPanel ? 6 : 12}
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            transition: 'all .5s ease-in-out',
          }}>
          <ChatHistory
            patientId={patientId}
            citationSelection={citationSelection}
            setCitationSelection={handleCitationSelection}
            isPopoverLayout={isPopoverLayout}
            onSelectedConversationIdChange={onSelectedConversationIdChange}
            onConversationSelected={onConversationSelected}
            onScrollStateChange={onScrollStateChange}
            renderHistoryWrapper={renderHistoryWrapper}
            startNewConversationRef={startNewConversationRef}
            openLetterCanvas={openLetterCanvas}
            updateLetterCanvas={updateLetterCanvas}
            getActiveDocumentHtml={getActiveDocumentHtml}
            isLetterCanvasOpen={citationContent?.documentType === 'letter'}
            letterConversationId={letterConversationId}
            letterType={letterType}
            hasDocumentGenerationFeature={hasDocumentGenerationFeature}
            setAiLetterId={setAiLetterId}
            setOnLetterSaved={setOnLetterSaved}
            onReceivingResponseChange={setIsReceivingResponse}
          />
        </Grid>
        {!isPopoverLayout && (
          <Grid item xs={showSideCitationPanel ? 6 : 0} sx={{ height: '100vh' }}>
            {citationContent && (
              <CitationPanel
                citation={{
                  ...citationContent,
                  emrDocumentId: aiLetterData?.aiLetter?.emrDocumentId,
                  updatedAt: isLetterCanvasOpen ? aiLetterData?.aiLetter?.updatedAt : undefined,
                }}
                onClose={isLetterCanvasOpen ? closeLetterCanvas : () => handleCitationSelection(null)}
              />
            )}
          </Grid>
        )}
        {isPopoverLayout && (
          <CitationOverlay
            open={showOverlayCitationPanel}
            citations={citationSelectionItems.map((citation) => ({
              ...citation,
              emrDocumentId:
                citation.documentType === 'letter' ? aiLetterData?.aiLetter?.emrDocumentId : citation.emrDocumentId,
              updatedAt: citation.documentType === 'letter' ? aiLetterData?.aiLetter?.updatedAt : undefined,
            }))}
            activeIndex={citationSelectionActiveIndex}
            onClose={isLetterCanvasOpen ? closeLetterCanvas : () => handleCitationSelection(null)}
            onActiveIndexChange={handleActiveIndexChange}
          />
        )}
      </Grid>
    </>
  );
};

export default ChatContainer;
